import { noop } from 'lodash';
import { createContext, memo, type PropsWithChildren, useContext, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { SEARCH_INITIAL_HEADER_HEIGHT, WKSPC_VIEW_CONTROL_HEIGHT } from '~/constants/WorkspaceSpacing';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type SearchHeaderHeightProviderType = {
  searchHeaderHeight: number;
  setSearchHeaderHeight: (height: number) => void;
  viewControlsHeight: number;
  setViewControlsHeight: (height: number) => void;
};

const defaultValue: SearchHeaderHeightProviderType = {
  searchHeaderHeight: 0,
  setSearchHeaderHeight: noop,
  viewControlsHeight: 0,
  setViewControlsHeight: noop,
};

export const SearchHeaderHeightContext = createContext<SearchHeaderHeightProviderType>(defaultValue);

export const SearchHeaderHeightProvider = memo(({ children }: PropsWithChildren<object>) => {
  const [searchHeaderHeight, setSearchHeaderHeight] = useState(SEARCH_INITIAL_HEADER_HEIGHT);
  const [viewControlsHeight, setViewControlsHeight] = useState(WKSPC_VIEW_CONTROL_HEIGHT);

  const value = useMemo<SearchHeaderHeightProviderType>(
    () => ({
      searchHeaderHeight,
      setSearchHeaderHeight,
      viewControlsHeight,
      setViewControlsHeight,
    }),
    [searchHeaderHeight, viewControlsHeight],
  );

  return <SearchHeaderHeightContext.Provider value={value}>{children}</SearchHeaderHeightContext.Provider>;
});

SearchHeaderHeightProvider.displayName = 'SearchHeaderHeightProvider';

export const useSearchHeaderHeightContext = () => {
  const context = useContext(SearchHeaderHeightContext);

  if (context === defaultValue) {
    const error = 'SearchHeaderHeightContext used outside of SearchHeaderHeightProvider';
    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
